import { useEffect, useMemo, useState } from 'react';
import { ArrowRight } from '../components/icons';
import { useParams } from 'react-router-dom';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Card from '../components/Card';
import Modal from '../components/Modal';
import { crmService } from '../services/crmService';
import type { Empresa, MensagemPessoa, Pessoa } from '../types';

type Tab = 'Resumo' | 'Pessoas' | 'Histórico';

export default function EmpresaDetalhePage() {
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('Resumo');
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [selectedPessoa, setSelectedPessoa] = useState<Pessoa | null>(null);
  const [chat, setChat] = useState<MensagemPessoa[]>([]);
  const [message, setMessage] = useState('');
  const [canal, setCanal] = useState<'email' | 'telefone'>('email');

  useEffect(() => {
    if (!id) return;
    crmService.getEmpresaById(id).then(setEmpresa);
    crmService.getPessoasByEmpresa(id).then(setPessoas);
  }, [id]);

  useEffect(() => {
    if (!selectedPessoa) return;
    crmService.getMensagensPessoa(selectedPessoa.id).then(setChat);
  }, [selectedPessoa]);

  const historicoResumo = useMemo(() => chat.slice(-8), [chat]);

  const send = async () => {
    if (!selectedPessoa || message.trim().length < 1) return;
    await crmService.sendMensagem(selectedPessoa.id, canal, message.trim(), 'out');
    setMessage('');
    const next = await crmService.getMensagensPessoa(selectedPessoa.id);
    setChat(next);
  };

  if (!empresa) return <Card>Empresa não encontrada.</Card>;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">{empresa.nome_empresa}</h1>
      <div className="flex gap-2">
        {(['Resumo', 'Pessoas', 'Histórico'] as Tab[]).map((item) => (
          <Button key={item} variant={tab === item ? 'primary' : 'outline'} onClick={() => setTab(item)}>
            {item}
          </Button>
        ))}
      </div>

      {tab === 'Resumo' ? (
        <Card className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-sm text-slate-500">Telefone</p>
            <p className="font-semibold">{empresa.telefone}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Website</p>
            <p className="font-semibold">{empresa.website}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Endereço</p>
            <p className="font-semibold">{empresa.endereco}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Rating / Reviews</p>
            <p className="font-semibold">
              {empresa.rating} ({empresa.reviews})
            </p>
          </div>
        </Card>
      ) : null}

      {tab === 'Pessoas' ? (
        <div className="grid gap-3 md:grid-cols-2">
          {pessoas.map((p) => (
            <Card key={p.id}>
              <div className="flex items-start gap-3">
                <img src={p.foto_linkedin} alt={p.nome} className="h-12 w-12 rounded-full object-cover" />
                <div className="min-w-0">
                  <p className="font-semibold">{p.nome}</p>
                  <p className="text-sm text-slate-500">{p.cargo}</p>
                  <p className="text-xs text-slate-500">{p.setor}</p>
                  <p className="mt-2 text-xs">{p.email}</p>
                  <p className="text-xs">{p.telefone}</p>
                  <div className="mt-2 flex gap-2">
                    <Badge variant={p.status_email ? 'success' : 'neutral'}>Email {p.status_email ? 'ok' : 'pendente'}</Badge>
                    <Badge variant={p.status_telefone ? 'success' : 'neutral'}>Telefone {p.status_telefone ? 'ok' : 'pendente'}</Badge>
                  </div>
                </div>
              </div>
              <Button className="mt-3" variant="outline" onClick={() => setSelectedPessoa(p)}>
                Conversas
              </Button>
            </Card>
          ))}
        </div>
      ) : null}

      {tab === 'Histórico' ? (
        <Card className="space-y-2">
          <p className="font-semibold">Últimas interações</p>
          {historicoResumo.length === 0 ? <p className="text-sm text-slate-500">Sem histórico.</p> : null}
          {historicoResumo.map((m) => (
            <div key={m.id} className="rounded-xl border border-borderLight p-3 text-sm dark:border-borderDark">
              <p className="font-medium">{m.canal.toUpperCase()}</p>
              <p>{m.mensagem}</p>
              <p className="mt-1 text-xs text-slate-500">{new Date(m.created_at).toLocaleString('pt-BR')}</p>
            </div>
          ))}
        </Card>
      ) : null}

      <Modal open={!!selectedPessoa} onClose={() => setSelectedPessoa(null)} title={`Conversas · ${selectedPessoa?.nome || ''}`}>
        <div className="space-y-3">
          <div className="max-h-72 space-y-2 overflow-y-auto rounded-xl border border-borderLight p-3 dark:border-borderDark">
            {chat.length === 0 ? <p className="text-sm text-slate-500">Sem mensagens ainda.</p> : null}
            {chat.map((m) => (
              <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.direction === 'out' ? 'ml-auto bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>
                <p>{m.mensagem}</p>
                <p className={`mt-1 text-[10px] ${m.direction === 'out' ? 'text-white/80' : 'text-slate-500'}`}>
                  {m.canal} · {new Date(m.created_at).toLocaleString('pt-BR')}
                </p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[110px_1fr] gap-2">
            <select
              value={canal}
              onChange={(e) => setCanal(e.target.value as 'email' | 'telefone')}
              className="h-11 rounded-xl border border-borderLight bg-cardLight px-2 text-sm dark:border-borderDark dark:bg-cardDark"
            >
              <option value="email">Email</option>
              <option value="telefone">Telefone</option>
            </select>
            <input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Digite a mensagem..."
              className="h-11 rounded-xl border border-borderLight bg-cardLight px-3 text-sm dark:border-borderDark dark:bg-cardDark"
            />
          </div>
          <Button variant="cta" onClick={send} iconLeft={<ArrowRight />}>
            Enviar mensagem
          </Button>
        </div>
      </Modal>
    </div>
  );
}
